/*!
 * Expand/collapse animation based on Vue's transition approach
 * https://github.com/vuejs/vue-next/blame/master/packages/runtime-dom/src/components/Transition.ts
 * 
 * Usage in CSS:
    .is-expanding-enter-active,
    .is-expanding-leave-active {
        transition: height ease-in-out 0.5s, opacity ease-in-out 0.5s;
        overflow: hidden;
    }

    .is-expanding-enter-from,
    .is-expanding-leave-to {
        opacity: 0;
    }
 */

/**
 * Get height of expanded element, used as transition target
 * @params {HTMLElement} element
 * @returns {Number}
 */
function getExpandedHeight(element) {
	element.hidden = false;

	const expandedHeight = element.scrollHeight;

	element.hidden = true;

	return expandedHeight;
}

/**
 * Execute function in next frame
 * @params {Function} fn
 */
function nextFrame(fn) {
	requestAnimationFrame(() => {
		requestAnimationFrame(fn);
	});
}

/**
 * Synchronously force layout to put elements into a certain state
 */
function forceReflow() {
	return document.body.offsetHeight;
}

/**
 * Collapse element
 * @params {HTMLElement} element
 * @params {Object} [options]
 * @params {Boolean} [options.skipAnimation=false]
 * @returns {Promise}
 */
function collapse(element, options = {}) {
	if (options.skipAnimation) {
		element.hidden = true;

		return Promise.resolve();
	}

	const currentHeight = element.offsetHeight;

	element.style.height = `${currentHeight}px`;

	element.classList.add('is-expanding-leave-from');

	forceReflow();

	element.classList.add('is-expanding-leave-active');

	return new Promise(resolve => {
		const callback = function () {
			element.removeEventListener('transitionend', callback);

			element.classList.remove('is-expanding-leave-to');
			element.classList.remove('is-expanding-leave-active');

			element.hidden = true;
			element.style.height = '';

			resolve();
		};

		nextFrame(() => {
			element.classList.remove('is-expanding-leave-from');
			element.classList.add('is-expanding-leave-to');

			element.style.height = '0px';

			element.addEventListener('transitionend', callback);
		});
	});
}

/**
 * Expand element to its real height
 * @params {HTMLElement} element
 * @params {Object} [options]
 * @params {Boolean} [options.skipAnimation=false]
 * @returns {Promise}
 */
function expand(element, options = {}) {
	if (options.skipAnimation) {
		element.hidden = false;

		return Promise.resolve();
	}

	const expandedHeight = getExpandedHeight(element);

	element.hidden = false;
	element.style.height = '0px';

	element.classList.add('is-expanding-enter-from');

	forceReflow();

	element.classList.add('is-expanding-enter-active');

	return new Promise(resolve => {
		const callback = function () {
			element.removeEventListener('transitionend', callback);

			element.classList.remove('is-expanding-enter-to');
			element.classList.remove('is-expanding-enter-active');

			element.style.height = '';

			resolve();
		};

		nextFrame(() => {
			element.classList.remove('is-expanding-enter-from');
			element.classList.add('is-expanding-enter-to');

			element.style.height = `${expandedHeight}px`;

			element.addEventListener('transitionend', callback);
		});
	});
}

/**
 * Expand/collapse element
 * @params {HTMLElement} element
 * @params {Object} [options]
 * @params {Boolean} [options.skipAnimation=false]
 * @returns {Promise}
 */
function toggle(element, options = {}) {
	if (element.hidden) {
		return expand(element, options);
	}

	return collapse(element, options);
}

export { toggle, expand, collapse };
